import React, { useCallback, useState, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import { TextBox, Button, theme } from "uikit";
import { useTranslation } from "react-i18next";

import { useTypedSelector } from "../../redux/store";
import Session from "../../redux/services/Session";
import { useKeyBind } from "../../hooks";
import { StyledColumn } from "../../components/common";
import { useNotification } from "../../components/NotificationProvider";
import {
	useConnectionText,
	ConnectionStatus,
} from "../../components/ConnectionQuality";
import { LOGIN_TEXTS } from "../../components/LoginLanguage";

const Root = styled(StyledColumn)`
	padding: 20px;

	border: 1px solid #f2f2f2;
	border-radius: 5px;

	box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.1);
`;

const Title = styled.div`
	font-family: "Lato";
	font-style: normal;
	font-weight: 700;
	font-size: 16px;
	line-height: 19px;
	text-align: center;

	color: ${theme.colors.primary};
`;

const Label = styled.div`
	font-family: "Lato";
	font-style: normal;
	font-weight: 400;
	font-size: 14px;
	line-height: 17px;

	color: ${theme.colors.secondary};
`;

const LoginPage: React.FC = () => {
	const dispatch = useDispatch();
	const { i18n } = useTranslation();
	const { error } = useNotification();

	// Built-in texts: before login the server gives no translations.
	const language = useTypedSelector((state) => state.session.language);
	const texts = LOGIN_TEXTS[language] ?? LOGIN_TEXTS.en;
	const connectionText = useConnectionText(language);

	const translateTexts = useMemo(
		() => ({
			error: texts.errorTitle,
			invalidCredentials: texts.invalidCredentials,
			emptyUsername: texts.emptyUsername,
			emptyPassword: texts.emptyPassword,
			noLinkTitle: texts.errorTitle,
			offline: connectionText("hint.offline"),
			noServer: connectionText("hint.noServer"),
		}),
		[texts, connectionText],
	);

	const connected = useTypedSelector((state) => state.prpc.connected);
	const online = useTypedSelector((state) => state.connectionQuality.online);

	const languageUser = useTypedSelector(
		(state) => state.account.user?.settings?.language,
	);

	const [username, setUsername] = useState<string>("");
	const [password, setPassword] = useState<string>("");
	const [translate, setTranslate] = useState<boolean>(false);

	const login = useCallback(() => {
		const isNotValidationAuthLogin =
			typeof username !== "string" || username.trim() === "";

		const isNotValidationAuthPassword =
			typeof password !== "string" || password.trim() === "";

		if (isNotValidationAuthLogin || isNotValidationAuthPassword) {
			error(
				translateTexts.error,
				isNotValidationAuthLogin
					? translateTexts.emptyUsername
					: translateTexts.emptyPassword,
			);
			return;
		}

		// No socket: tell why the login may fail, but still try as before.
		// Only the socket counts: `navigator.onLine` can be wrong, and a live
		// socket proves the network is there.
		if (!connected) {
			error(
				translateTexts.noLinkTitle,
				online ? translateTexts.noServer : translateTexts.offline,
			);
		}

		setTranslate(true);
		dispatch(
			Session.login(username, password, () =>
				error(translateTexts.error, translateTexts.invalidCredentials),
			),
		);
	}, [
		username,
		password,
		online,
		connected,
		dispatch,
		error,
		translateTexts.error,
		translateTexts.emptyUsername,
		translateTexts.emptyPassword,
		translateTexts.invalidCredentials,
		translateTexts.noLinkTitle,
		translateTexts.offline,
		translateTexts.noServer,
	]);

	useKeyBind(["Enter"], (event) => {
		login();

		event.stopPropagation();
		event.preventDefault();
	});

	const getTranslate = useCallback(() => {
		setTranslate(false);
		if (languageUser) i18n.changeLanguage(languageUser);
		else i18n.changeLanguage(language);
	}, [i18n, language, languageUser]);

	useEffect(() => {
		let timeoutId: NodeJS.Timeout;

		if (translate) {
			timeoutId = setTimeout(() => {
				getTranslate();
			}, 1000);
		}

		return () => {
			if (timeoutId) {
				clearTimeout(timeoutId);
			}
		};
	}, [getTranslate, translate]);

	return (
		<Root gap="20px">
			<Title>{texts.signIn}</Title>

			<StyledColumn gap="10px">
				<Label>{texts.login}</Label>
				<TextBox.TextBox
					name="login"
					value={username}
					onChange={setUsername}
					placeholder={texts.usernamePlaceholder}
				/>

				<Label>{texts.password}</Label>
				<TextBox.TextBox
					type="password"
					name="password"
					value={password}
					onChange={setPassword}
					placeholder="********"
				/>
			</StyledColumn>

			<Button.Button text={texts.loginButton} onClick={login} />

			<ConnectionStatus lang={language} />
		</Root>
	);
};

export default LoginPage;
